import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ThumbsUp, Reply, Edit, Trash2, AlertTriangle, MessageSquare } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { ForumComentario } from '../../../types/forum';
import { cn } from '../../../lib/utils';
import { useAuth } from '../../../contexts/AuthContext';

interface ForumComentarioItemProps {
  comentario: ForumComentario;
  onLike: (comentarioId: string) => void;
  onReply: (comentarioId: string, conteudo: string) => Promise<void>;
  onEdit: (comentarioId: string, conteudo: string) => Promise<void>;
  onDelete: (comentarioId: string) => Promise<void>;
  onModerate: (comentarioId: string, ativo: boolean) => Promise<void>;
  canModerate: boolean;
  canDelete: boolean;
  isNested?: boolean;
}

export function ForumComentarioItem({
  comentario,
  onLike,
  onReply,
  onEdit,
  onDelete,
  onModerate,
  canModerate,
  canDelete,
  isNested = false,
}: ForumComentarioItemProps) {
  const { militar } = useAuth();
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [editContent, setEditContent] = useState(comentario.conteudo);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOwner = militar?.id === comentario.militar_id;
  const canEdit = isOwner && comentario.ativo;

  const timeAgo = formatDistanceToNow(new Date(comentario.criado_em), {
    addSuffix: true,
    locale: ptBR,
  });

  const habboAvatar = (nick: string, size = 'b') =>
    `https://www.habbo.com.br/habbo-imaging/avatarimage?user=${encodeURIComponent(nick)}&action=std&direction=4&head_direction=2&img_format=png&gesture=std&headonly=1&size=${size}`;

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    setIsSubmitting(true);
    try {
      await onReply(comentario.id, replyContent);
      setReplyContent('');
      setIsReplying(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim()) return;
    setIsSubmitting(true);
    try {
      await onEdit(comentario.id, editContent);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await onDelete(comentario.id);
      setShowDeleteDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn(
      "border-l-2 pl-4",
      isNested ? "border-muted ml-8 mt-3" : "border-primary/30",
      !comentario.ativo && "opacity-60"
    )}>
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm">
            {comentario.militar?.nick ? (
              <img
                src={habboAvatar(comentario.militar.nick)}
                alt={comentario.militar.nick}
                title={comentario.militar.nick}
                className="w-10 h-10 flex-shrink-0"
              />
            ) : null}
            {comentario.militar?.nick ? (
              <a
                href={`https://www.exercitobrhb.net/perfil/${comentario.militar.nick}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline hover:text-primary transition-colors"
              >
                {comentario.militar?.patente?.patente} {comentario.militar?.nick}
              </a>
            ) : (
              <span className="font-medium">Anônimo</span>
            )}
            <span className="text-muted-foreground">•</span>
            <span className="text-muted-foreground">{timeAgo}</span>
            {!comentario.ativo && (
              <span className="text-destructive text-xs font-medium">(Inativo)</span>
            )}
          </div>

          {isEditing ? (
            <div className="mt-2 space-y-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Editar comentário..."
                rows={3}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleEdit} disabled={isSubmitting || !editContent.trim()}>
                  Salvar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-1 text-sm whitespace-pre-wrap">{comentario.conteudo}</p>
          )}

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-7 px-2 text-xs",
                comentario.user_liked && "text-primary"
              )}
              onClick={() => onLike(comentario.id)}
            >
              <ThumbsUp className={cn("h-3 w-3 mr-1", comentario.user_liked && "fill-current")} />
              {comentario.likes_count || comentario.likes?.length || 0}
            </Button>

            {!isNested && comentario.ativo && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setIsReplying(!isReplying)}
              >
                <Reply className="h-3 w-3 mr-1" />
                Responder
              </Button>
            )}

            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setIsEditing(true)}
              >
                <Edit className="h-3 w-3 mr-1" />
                Editar
              </Button>
            )}

            {canModerate && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onModerate(comentario.id, !comentario.ativo)}
              >
                <AlertTriangle className="h-3 w-3 mr-1" />
                {comentario.ativo ? 'Inativar' : 'Ativar'}
              </Button>
            )}

            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Excluir
              </Button>
            )}
          </div>

          {isReplying && (
            <div className="mt-3 space-y-2">
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Escreva sua resposta..."
                rows={2}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleReply} disabled={isSubmitting || !replyContent.trim()}>
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Responder
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsReplying(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Respostas aninhadas */}
      {comentario.respostas && comentario.respostas.length > 0 && (
        <div className="mt-3 space-y-3">
          {comentario.respostas.map((resposta) => (
            <ForumComentarioItem
              key={resposta.id}
              comentario={resposta}
              onLike={onLike}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onModerate={onModerate}
              canModerate={canModerate}
              canDelete={canDelete}
              isNested
            />
          ))}
        </div>
      )}

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir comentário?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Esta ação não pode ser desfeita. O comentário será permanentemente excluído.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isSubmitting}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
